// The screen for somebody who is not signed in.
//
// It is a SCREEN rather than a message, because not being signed in is not a
// kind of failure. The day screen's failure state offers "Try again", which is
// the right answer to a server that was down and the wrong one to a 401: trying
// again re-sends the same sessionless request, gets the same 401, and draws the
// same words - a loop with no way out, which is what the captain hit on the
// first day the app was live.
//
// The layout is the prototype's sign-in gate (docs/design/prototype-v1.md 6,
// `screenshots/phone-signin.png`), ported rather than reinvented: the brand mark
// over a serif wordmark, one line saying what the product is, and one action.
//
// One thing the prototype's gate does not have: a line saying why the last
// attempt did not work, drawn from the marker the server left on the URL
// (`outcome.ts`). It is a decoration on this screen and never the reason for it -
// the screen is raised by a 401 and by nothing else. This screen is also the one
// that takes the marker back off the address, because it is the one that says
// it: `outcome.ts` carries the reason a sentence must not outlive its attempt.
//
// What is deliberately NOT here: the prototype's three promises - "Free. No paid
// tier, now or later.", "No AI, anywhere in the app.", "No advertising and no
// tracking." - which prototype-v1.md 5 calls "the cheapest trust the product can
// buy". They are approved, and the brief that asked for this screen asked for
// the product's name, one line and one action, so they are left out rather than
// smuggled in under an approval this screen was not given. Restoring them is
// three rows under the button plus the prototype's `.promise` block, which is
// the one part of its gate CSS styles.css does not carry.

import { useEffect, useState } from 'react';
import { signInUrl } from '../api/client.ts';
import { BrandMark } from '../day/Icon.tsx';
import { forgetSignInOutcome, signInOutcome } from './outcome.ts';

/**
 * Google's own mark, at Google's own proportions.
 *
 * Drawn rather than fetched, like every other glyph here - and unlike every
 * other glyph here it is four filled paths in fixed brand colours rather than
 * one stroked path in `currentColor`, because it is somebody else's mark. It
 * does not take a theme, and it must not be restyled to take one.
 */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export function SignedOut() {
  // What the last attempt did, if there was one. Read here rather than passed
  // in, because it is not application state: it is on the URL before any of this
  // runs, and there is nothing that can change it without a navigation.
  //
  // Read ONCE, into state, and then taken off the address below. Held rather
  // than re-read because the two have to be able to disagree: the address is
  // clean a moment later, and a sentence that vanished half way through being
  // read - on whatever unrelated state settled behind this screen - would be a
  // worse failure than the stale one this closes. The outcome belongs to the
  // arrival; the arrival lasts as long as this screen does.
  const [outcome] = useState(() => signInOutcome());

  // After the read, never before it. `outcome.ts` says why the address must not
  // go on saying this once it has been said.
  useEffect(() => {
    forgetSignInOutcome();
  }, []);

  return (
    <div className="app">
      <div className="gate">
        <div className="gate-in">
          <BrandMark />
          <h1>Perfect Note</h1>
          <p className="tag">
            Your day, in the shape you gave it. Tick things off, keep your notes, and look back
            whenever you want.
          </p>

          {/*
            The server would not make them an account.

            A plain sentence, above the button and not instead of it. Somebody
            refused here has done nothing wrong and has nothing to fix, so it
            says what happened and stops - no code, no "try again later", no
            support address this app does not have.

            THE BUTTON STAYS. Being turned away is about the Google account that
            was offered, not about this browser: somebody who already has an
            account here signs in from this very screen, and so does the owner.
            Hiding the action would lock out the one person who could free a
            place, which is the failure this whole screen exists to have ended.

            An ordinary paragraph, and NOT a live region. This whole screen is
            mounted at once with its text already in it, and a live region that
            arrives that way is routinely not announced - the same trap the
            toast in App.tsx is mounted empty to avoid. Here there is nothing to
            work around: the sentence sits between the tagline and the button, so
            it is read on the way to the action either way.
          */}
          {outcome === 'accounts-full' && (
            <p className="gatenotice">
              Perfect Note is not taking new accounts at the moment. If you already have one, you
              can still sign in below.
            </p>
          )}

          {/*
            An anchor, not a button with an onClick.

            Signing in is a NAVIGATION: `/api/v1/auth/google/start` answers 302 to
            Google, and the browser has to be the thing that follows it, or the
            user never reaches a page they can consent on. An anchor is what the
            browser already knows how to do that with - and it comes with the
            behaviour a hand-rolled click handler quietly drops: it is reachable
            by keyboard as a link, it is announced as one, it takes a middle
            click and a right click, and it still works if the script that would
            have handled the click never ran.
          */}
          <a className="gbtn" href={signInUrl}>
            <GoogleMark />
            Sign in with Google
          </a>

          {/* True, and worth saying at the moment somebody is deciding: sign-in
              requests `openid email profile` and nothing else, which is also
              what keeps this app out of Google's app-verification queue
              (AGENTS.md, "No calendar overlay"). */}
          <p className="finetext">Signing in reads your name and email address only.</p>
        </div>
      </div>
    </div>
  );
}
